import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import { post } from '@rails/request.js'

export default class extends Controller {
  static targets = ['input', 'label', 'photoArea', 'spinner']
  static values = {
    maxFiles: { type: Number, default: 1 },
    maxFilesError: { type: String, default: '❌ A maximum of %{maxFiles} files are permitted.' },
    maxFileSize: { type: Number, default: 3 },
    maxFileSizeError: { type: String, default: '❌ The maximum file size of %{maxFileSize}MB has been exceeded.' },
    uploadError: { type: String, default: '❌ File upload error.' },
    directUploadUrl: String,
    uploadingClass: { type: String, default: '--uploading' },
    dragClass: { type: String, default: '--drag' },
    callbackUrl: String
  }

  connect () {
    this._handleDragOver = this._handleDragOver.bind(this)
    this._handleDragEnter = this._handleDragEnter.bind(this)
    this._handleDragLeave = this._handleDragLeave.bind(this);

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.inputTarget.addEventListener(eventName, this._handleDragOver)
      this.labelTarget.addEventListener(eventName, this._handleDragOver)
    })

    this.labelTarget.addEventListener('dragenter', this._handleDragEnter)
    this.labelTarget.addEventListener('dragleave', this._handleDragLeave)
    this.labelTarget.addEventListener('drop', this._handleDragLeave)
  }

  async upload (event) {
    event.preventDefault()
    const files = event.dataTransfer ? event.dataTransfer.files : event.target.files

    let errorMessage = ''

    if (files.length > this.maxFilesValue) {
      errorMessage += this.maxFilesErrorValue.replace('%{maxFiles}', this.maxFilesValue) + '\n'
    }

    const hasLargeFile = Array.from(files).some(file => file.size > this.maxFileSizeValue * 1024 * 1024)
    if (hasLargeFile) {
      errorMessage += this.maxFileSizeErrorValue.replace('%{maxFileSize}', this.maxFileSizeValue) + '\n'
    }

    if (errorMessage) {
      window.alert(errorMessage.trim())
      this.inputTarget.value = null
      return
    }

    try {
      this._showLoadingSpinner()
      await Promise.all(Array.from(files).map(file => this._uploadFile(file)))

      this.inputTarget.value = null
      if (this.hasCallbackUrlValue) {
        this._turboStreamPost(this.callbackUrlValue, { blob_ids: this._getFieldValues() })
      } else {
        this.inputTarget.form.requestSubmit()
      }
    } catch {
      window.alert(this.uploadErrorValue)
      this._hideLoadingSpinner()
    }
  }

  _handleDragOver (event) {
    event.preventDefault()
    event.stopPropagation()

    if (event.dataTransfer.files.length > 0) this.upload(event)
  }

  _handleDragEnter () {
    this.labelTarget.classList.add(this.dragClassValue)
  }

  _handleDragLeave (event) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    this.labelTarget.classList.remove(this.dragClassValue)
  }

  _uploadFile (file) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.directUploadUrlValue)

      upload.create((error, blob) => {
        if (error) {
          reject(new Error('Upload went wrong'))
        } else {
          let hiddenField = this.inputTarget.nextElementSibling
          if (hiddenField && hiddenField.name === this.inputTarget.name) {
            hiddenField.value = `${hiddenField.value},${blob.signed_id}`
          } else {
            hiddenField = document.createElement('input')
            hiddenField.setAttribute('type', 'hidden')
            hiddenField.name = this.inputTarget.name
            hiddenField.setAttribute('value', blob.signed_id)
            hiddenField.setAttribute('id', `blob_id_${blob.id}`)
            this.inputTarget.after(hiddenField)
          }
          resolve()
        }
      })
    })
  }

  _getFieldValues () {
    const form = this.inputTarget.closest('form')

    if (form) {
      const fieldName = this.inputTarget.name
      const matchingInputs = form.querySelectorAll(`[name="${fieldName}"]`)
      const values = Array.from(matchingInputs)
        .map(input => input.value)
        .filter(value => value.trim() !== '')

      return values
    }
    return null
  }

  async _turboStreamPost (url, body) {
    await post(url, {
      body: JSON.stringify(body),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  _showLoadingSpinner () {
    if (this.hasSpinnerTarget && this.hasPhotoAreaTarget) {
      this.photoAreaTarget.classList.add('hidden')
      this.spinnerTarget.classList.remove('hidden')
    }
  }

  _hideLoadingSpinner () {
    if (this.hasSpinnerTarget && this.hasPhotoAreaTarget) {
      this.spinnerTarget.classList.add('hidden')
      this.photoAreaTarget.classList.remove('hidden')
    }
  }

  disconnect () {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.inputTarget.removeEventListener(eventName, this._handleDragOver)
      this.labelTarget.removeEventListener(eventName, this._handleDragOver)
    })

    this.labelTarget.removeEventListener('dragenter', this._handleDragEnter)
    this.labelTarget.removeEventListener('dragleave', this._handleDragLeave)
    this.labelTarget.removeEventListener('drop', this._handleDragLeave)
  }
}
